import { AnalyticStore } from "../analytics/analytic_store.js";
import { ensureSignupTimestampIso } from "../analytics/analytics_identity.js";
import { TelegramAPISettings } from "../telegram_api_settings.js";
import { getStoredDeviceId } from "../utils/device_id.js";
import { VersionLoader } from "../version_loader.js";
import type { S2SConsentSnapshot, S2SEventEnvelope, S2SIdentifiers } from "../socket_handler/data_structures/to_server/custom_event_request.js";

const APP_ID = 'com.matchstars.royalsmash';
const SCHEMA_VERSION = '1.0';
const SDK_VERSION = 'royalsmash-s2s-1.0';
const CONTEXT_STORAGE_KEY = 's2s_context_v1';
const CONSENT_STORAGE_KEY = 's2s_consent_snapshot_v1';
const MAX_CONSENT_ID_LENGTH = 128;
const MAX_CONSENT_TEXT_LENGTH = 64;
const MAX_CONSENT_FUTURE_SKEW_MS = 5 * 60_000;
const ATT_STATUSES = new Set( [
  'authorized',
  'denied',
  'restricted',
  'not_determined',
  'not_applicable',
  '0',
  '1',
  '2',
  '3',
] );

type PersistedContext = {
  first_open_time: string;
  device?: string;
  appsflyer_id?: string;
  gaid?: string;
  gclid?: string;
  market_referrer_gclid?: string;
  gbraid?: string;
};

type ContextUpdate = {
  device?: string;
  appsflyer_id?: string;
  gaid?: string;
  gclid?: string;
  market_referrer_gclid?: string;
  gbraid?: string;
};

function makeUuid(): string
{
  try
  {
    const uuid = globalThis.crypto?.randomUUID?.();
    if ( uuid ) return uuid;

    const bytes = new Uint8Array( 16 );
    if ( globalThis.crypto?.getRandomValues )
      globalThis.crypto.getRandomValues( bytes );
    else
      for ( let index = 0; index < bytes.length; index++ )
        bytes[index] = Math.floor( Math.random() * 256 );
    bytes[6] = ( bytes[6] & 0x0f ) | 0x40;
    bytes[8] = ( bytes[8] & 0x3f ) | 0x80;
    const hex = Array.from( bytes, value => value.toString( 16 ).padStart( 2, '0' ) ).join( '' );
    return `${hex.slice( 0, 8 )}-${hex.slice( 8, 12 )}-${hex.slice( 12, 16 )}-${hex.slice( 16, 20 )}-${hex.slice( 20 )}`;
  }
  catch {}

  // Extremely old WebViews can still reach this path if crypto access throws.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace( /[xy]/g, character =>
  {
    const random = Math.floor( Math.random() * 16 );
    return ( character === 'x' ? random : ( random & 0x3 ) | 0x8 ).toString( 16 );
  } );
}

function parseStoredObject<T>( key: string ): T | null
{
  try
  {
    const raw = localStorage.getItem( key );
    if ( !raw ) return null;
    const parsed: unknown = JSON.parse( raw );
    return parsed && typeof parsed === 'object' ? parsed as T : null;
  }
  catch
  {
    return null;
  }
}

function storeObject( key: string, value: unknown ): boolean
{
  try
  {
    localStorage.setItem( key, JSON.stringify( value ) );
    return true;
  }
  catch ( error )
  {
    console.warn( `[S2S] unable to persist ${key}`, error );
    return false;
  }
}

function nonEmpty( value: unknown ): string | undefined
{
  const normalized = String( value ?? '' ).trim();
  return normalized || undefined;
}

function isIsoDate( value: unknown ): value is string
{
  return typeof value === 'string' && value.length > 0 && Number.isFinite( Date.parse( value ) );
}

function getPlatform(): string
{
  try
  {
    const capacitor_platform = ( window as unknown as { Capacitor?: { getPlatform?: () => unknown } } )
      .Capacitor?.getPlatform?.();
    if ( capacitor_platform ) return String( capacitor_platform ).trim().toLowerCase();
  }
  catch {}

  try
  {
    const telegram_platform = ( window as unknown as { Telegram?: { WebApp?: { platform?: unknown } } } )
      .Telegram?.WebApp?.platform;
    if ( telegram_platform ) return String( telegram_platform ).trim().toLowerCase();
  }
  catch {}

  return 'web';
}

function resolveOsVersion( platform: string ): string
{
  try
  {
    const navigator_with_hints = navigator as Navigator & {
      userAgentData?: { platform?: string; platformVersion?: string };
    };
    const hinted_version = nonEmpty( navigator_with_hints.userAgentData?.platformVersion );
    if ( hinted_version && hinted_version !== '0.0.0' ) return hinted_version;

    const user_agent = navigator.userAgent ?? '';
    const match = platform === 'android'
      ? user_agent.match( /Android\s+([0-9]+(?:[._][0-9]+)*)/i )
      : platform === 'ios'
        ? user_agent.match( /(?:CPU(?: iPhone)? OS|iPhone OS)\s+([0-9]+(?:[._][0-9]+)*)/i )
        : platform === 'web'
          ? user_agent.match( /(?:Windows NT|Mac OS X)\s+([0-9]+(?:[._][0-9]+)*)/i )
          : null;
    const parsed_version = nonEmpty( match?.[1] )?.replace( /_/g, '.' );
    return parsed_version ?? 'unknown';
  }
  catch
  {
    return 'unknown';
  }
}

function validateConsentSnapshot( source: S2SConsentSnapshot ): S2SConsentSnapshot
{
  if ( !source || typeof source !== 'object' )
    throw new Error( 'A complete consent snapshot is required.' );

  const id = nonEmpty( source.id );
  const region = nonEmpty( source.region );
  const att_status = nonEmpty( source.att_status )?.toLowerCase();
  const policy_version = nonEmpty( source.policy_version );

  if ( !id || id.length > MAX_CONSENT_ID_LENGTH || /\s/.test( id ) )
    throw new Error( 'Consent snapshot id must contain 1-128 non-whitespace characters.' );
  if ( !region || region.length > MAX_CONSENT_TEXT_LENGTH )
    throw new Error( 'Consent region must contain 1-64 characters.' );
  if ( !att_status || !ATT_STATUSES.has( att_status ) )
    throw new Error( 'Consent ATT status is invalid.' );
  if ( !policy_version || policy_version.length > MAX_CONSENT_TEXT_LENGTH )
    throw new Error( 'Consent policy version must contain 1-64 characters.' );
  if (
    typeof source.analytics_allowed !== 'boolean'
    || typeof source.ad_measurement_allowed !== 'boolean'
    || typeof source.ad_personalization_allowed !== 'boolean'
  )
    throw new Error( 'All consent grant fields must be explicit booleans.' );
  if ( !isIsoDate( source.timestamp ) )
    throw new Error( 'Consent timestamp must be a valid ISO timestamp.' );

  const timestamp = new Date( source.timestamp ).toISOString();
  if ( Date.parse( timestamp ) > Date.now() + MAX_CONSENT_FUTURE_SKEW_MS )
    throw new Error( 'Consent timestamp cannot be more than five minutes in the future.' );

  return {
    id,
    region,
    att_status,
    analytics_allowed: source.analytics_allowed,
    ad_measurement_allowed: source.ad_measurement_allowed,
    ad_personalization_allowed: source.ad_personalization_allowed,
    timestamp,
    policy_version,
  };
}

function loadContext(): PersistedContext
{
  const stored = parseStoredObject<PersistedContext>( CONTEXT_STORAGE_KEY );
  const analytics_first_open = ensureSignupTimestampIso().signup_timestamp_iso;
  const first_open_time = isIsoDate( stored?.first_open_time )
    ? stored.first_open_time
    : analytics_first_open;

  return {
    first_open_time,
    device: nonEmpty( stored?.device ) ?? nonEmpty( getStoredDeviceId() ),
    appsflyer_id: nonEmpty( stored?.appsflyer_id ),
    gaid: nonEmpty( stored?.gaid ),
    gclid: nonEmpty( stored?.gclid ),
    market_referrer_gclid: nonEmpty( stored?.market_referrer_gclid ),
    gbraid: nonEmpty( stored?.gbraid ),
  };
}

function ensureFailClosedConsent( platform: string ): S2SConsentSnapshot
{
  const stored = parseStoredObject<S2SConsentSnapshot>( CONSENT_STORAGE_KEY );
  try
  {
    if ( stored ) return validateConsentSnapshot( stored );
  }
  catch {}

  const consent: S2SConsentSnapshot = {
    id: makeUuid(),
    region: 'unknown',
    att_status: platform === 'ios' ? 'not_determined' : 'not_applicable',
    analytics_allowed: false,
    ad_measurement_allowed: false,
    ad_personalization_allowed: false,
    timestamp: new Date().toISOString(),
    policy_version: 's2s-fail-closed-v1',
  };
  storeObject( CONSENT_STORAGE_KEY, consent );
  return consent;
}

export class S2SEventContextService
{
  public static initialize( update: ContextUpdate ): void
  {
    const current = loadContext();
    const next: PersistedContext = {
      first_open_time: current.first_open_time,
      device: nonEmpty( update.device ) ?? current.device,
      appsflyer_id: nonEmpty( update.appsflyer_id ) ?? current.appsflyer_id,
      gaid: nonEmpty( update.gaid ) ?? current.gaid,
      gclid: nonEmpty( update.gclid ) ?? current.gclid,
      market_referrer_gclid: nonEmpty( update.market_referrer_gclid ) ?? current.market_referrer_gclid,
      gbraid: nonEmpty( update.gbraid ) ?? current.gbraid,
    };
    storeObject( CONTEXT_STORAGE_KEY, next );
    ensureFailClosedConsent( getPlatform() );
  }

  public static clearAppsFlyerId(): void
  {
    const current = loadContext();
    const next: PersistedContext = { ...current };
    delete next.appsflyer_id;
    if ( !storeObject( CONTEXT_STORAGE_KEY, next ) )
      throw new Error( 'AppsFlyer identifier could not be cleared.' );
  }

  public static clearAdMeasurementIdentifiers(): void
  {
    const current = loadContext();
    const next: PersistedContext = {
      first_open_time: current.first_open_time,
      device: current.device,
      appsflyer_id: current.appsflyer_id,
    };
    if ( !storeObject( CONTEXT_STORAGE_KEY, next ) )
      throw new Error( 'Advertising measurement identifiers could not be cleared.' );
  }

  public static getStoredIdentifiers(): Omit<PersistedContext, 'first_open_time'> & { first_open_time: string }
  {
    return { ...loadContext() };
  }

  public static getConsentSnapshot(): S2SConsentSnapshot
  {
    return { ...ensureFailClosedConsent( getPlatform() ) };
  }

  /**
   * The CMP/ATT integration is the only supported path for changing consent.
   * Callers must pass the complete occurrence-time snapshot; this service does
   * not infer grants from MAX consent state, identifiers, region, or SDK availability.
   */
  public static applyExplicitConsentSnapshot( snapshot: S2SConsentSnapshot ): S2SConsentSnapshot
  {
    const validated = validateConsentSnapshot( snapshot );
    if ( !storeObject( CONSENT_STORAGE_KEY, validated ) )
      throw new Error( 'Consent snapshot could not be persisted.' );
    return { ...validated };
  }

  public static createEvent(
    event_name: string,
    properties: Record<string, unknown> = {},
    options: { transaction_id?: string } = {},
  ): S2SEventEnvelope
  {
    const context = loadContext();
    const platform = getPlatform();
    const consent = this.getConsentSnapshot();
    const appsflyer_id = nonEmpty( context.appsflyer_id );
    const identifiers: S2SIdentifiers = {
      ...( appsflyer_id ? { appsflyer_id } : {} ),
      ...( consent.ad_measurement_allowed && context.gaid ? { gaid: context.gaid } : {} ),
      ...( consent.ad_measurement_allowed && context.gclid ? { gclid: context.gclid } : {} ),
      ...( consent.ad_measurement_allowed && context.market_referrer_gclid ? { market_referrer_gclid: context.market_referrer_gclid } : {} ),
      ...( consent.ad_measurement_allowed && context.gbraid ? { gbraid: context.gbraid } : {} ),
    };
    const locale = String( TelegramAPISettings.getUserLanguage() ?? navigator.language ?? '' ).trim();
    const session_id = nonEmpty( AnalyticStore.getSessionIdFromServer() )
      ?? nonEmpty( AnalyticStore.getSessionId() )
      ?? 'unknown';

    return {
      payload: { ...properties, name: event_name },
      event_id: makeUuid(),
      event_time: new Date().toISOString(),
      first_open_time: context.first_open_time,
      schema_version: SCHEMA_VERSION,
      app_id: APP_ID,
      app_version: VersionLoader.getVersion(),
      platform,
      locale,
      device: context.device ?? 'unknown',
      session_id,
      sdk_version: SDK_VERSION,
      os_version: resolveOsVersion( platform ),
      ...( nonEmpty( options.transaction_id ) ? { transaction_id: nonEmpty( options.transaction_id ) } : {} ),
      consent,
      identifiers,
    };
  }
}
